import { useRef, FunctionComponent, ComponentProps, useState, useEffect } from 'react';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { MAX_OPACITY, MIN_OPACITY, PULSE_PERIOD } from '@/lib/globals';
import selectionFocusSound from '@/sounds/ob-sfx-move-selection-focus.mp3';
import { oscillate } from '@/lib/mathUtils';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { ClickTarget } from '@/components/ClickTarget';

type TextProps = {
    pulse?: boolean;
} & ComponentProps<typeof Text>;

const CLICKABLE_PADDING = [1.5, 1.25];

export const ClickableText: FunctionComponent<TextProps> = ({ pulse = true, onClick, ...props }) => {
    const textRef = useRef<TextProps>();
    const timer = useRef<number>(0);
    const [hovered, setHovered] = useState<boolean>(false);
    const [planeArgs, setPlaneArgs] = useState<[number, number]>([0, 0]);
    const [playSelectionFocusSound] = useOnlybotsSound(selectionFocusSound);
    const over = (e: ThreeEvent<PointerEvent>): void => {
        e.stopPropagation();
        playSelectionFocusSound();
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
        timer.current = 0;
    };

    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    useFrame((_, delta) => {
        if (pulse) {
            if (hovered && textRef.current) {
                timer.current += delta;
                textRef.current.fillOpacity = oscillate(MIN_OPACITY, MAX_OPACITY, timer.current, PULSE_PERIOD);
            } else if (!hovered && textRef.current && textRef.current.fillOpacity !== 1) {
                timer.current = 0;
                textRef.current.fillOpacity = 1;
            }
        }

        if (
            planeArgs[1] === 0 &&
            textRef.current?.geometry &&
            isFinite(textRef.current.geometry?.boundingBox?.max.y || Infinity)
        ) {
            const { boundingBox } = textRef.current.geometry;
            const textHeight = boundingBox ? boundingBox.max.y - boundingBox.min.y : 0;
            const textWidth = boundingBox ? boundingBox.max.x - boundingBox.min.x : 0;

            const target: [number, number] = [textWidth * CLICKABLE_PADDING[0], textHeight * CLICKABLE_PADDING[1]];
            setPlaneArgs(target);
        }
    });

    return (
        <ClickTarget
            width={planeArgs[0]}
            height={planeArgs[1]}
            position={props.position}
            onPointerOver={over}
            onPointerOut={out}
            onClick={onClick}
        >
            <Text
                ref={textRef}
                {...props}
                position={[0, -((props.fontSize || 0) * CLICKABLE_PADDING[1] - (props.fontSize || 0)) / 2, -0.0001]}
            >
                {props.children}
            </Text>
        </ClickTarget>
    );
};
